import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sparkles, Menu, X, ShoppingBag, User, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { getCartCount } = useCart();
  const { isAuthenticated, user } = useAuth();
  const { wishlistCount } = useWishlist();
  const cartCount = getCartCount();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/products', label: 'Products' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-purple-950/95 backdrop-blur-sm border-b border-purple-800">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <Sparkles className="w-8 h-8 text-yellow-400 group-hover:animate-spin" />
            <span className="text-2xl font-bold text-white">
              Infused by <span className="text-white">Daizy</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-lg font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-white'
                    : 'text-purple-200 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side Buttons (Desktop) */}
          <div className="hidden md:flex items-center gap-3">
            {/* Wishlist Button */}
            {isAuthenticated && (
              <Link to="/wishlist">
                <Button 
                  variant="ghost" 
                  className="text-purple-200 hover:text-white relative p-2"
                  data-testid="header-wishlist-btn"
                >
                  <Heart className="w-5 h-5" />
                  {wishlistCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                      {wishlistCount}
                    </span>
                  )}
                </Button>
              </Link>
            )}
            
            {/* Account Button */}
            <Link to={isAuthenticated ? "/account" : "/auth"}>
              <Button 
                variant="outline" 
                className="border-purple-500 text-purple-200 hover:bg-purple-800 hover:text-white"
                data-testid="header-account-btn"
              >
                <User className="w-4 h-4 mr-2" />
                {isAuthenticated ? user?.name?.split(' ')[0] : 'Sign In'}
              </Button>
            </Link>
            
            {/* Cart Button */}
            <Link to="/cart">
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-semibold relative">
                <ShoppingBag className="mr-2" />
                Cart
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-white text-purple-900 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                    {cartCount}
                  </span>
                )}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-yellow-400 p-2"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-6 border-t border-purple-800">
            <div className="flex flex-col space-y-4">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-lg font-medium transition-colors duration-200 ${
                    isActive(link.path)
                      ? 'text-white'
                      : 'text-purple-200 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link 
                to={isAuthenticated ? "/account" : "/auth"}
                onClick={() => setIsMenuOpen(false)}
              >
                <Button 
                  variant="outline" 
                  className="w-full border-purple-500 text-purple-200 hover:bg-purple-800"
                >
                  <User className="w-4 h-4 mr-2" />
                  {isAuthenticated ? 'My Account' : 'Sign In'}
                </Button>
              </Link>
              {isAuthenticated && (
                <Link to="/wishlist" onClick={() => setIsMenuOpen(false)}>
                  <Button 
                    variant="outline" 
                    className="w-full border-purple-500 text-purple-200 hover:bg-purple-800"
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Wishlist
                    {wishlistCount > 0 && (
                      <span className="ml-2 bg-red-500 text-white rounded-full px-2 py-0.5 text-xs font-bold">
                        {wishlistCount}
                      </span>
                    )}
                  </Button>
                </Link>
              )}
              <Link to="/cart" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-semibold relative">
                  <ShoppingBag className="mr-2" />
                  Cart
                  {cartCount > 0 && (
                    <span className="ml-2 bg-white text-purple-900 rounded-full px-2 py-0.5 text-xs font-bold">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
